import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import '../BanIpConfirm/BanIpConfirm.css';

const StreamBanConfirm = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const username = useMemo(() => searchParams.get('username') || '', [searchParams]);
  const password = useMemo(() => searchParams.get('password') || '', [searchParams]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadPreview() {
      if (!username || !password) {
        setError('This ban link is missing required information.');
        setLoading(false);
        return;
      }

      try {
        setError('');
        const query = new URLSearchParams({ username, password });
        const response = await fetch(`/api/v1/stream-ban/preview?${query.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || 'This ban link is invalid.');
        }

        const data = await response.json();
        setPreview(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'This ban link is invalid.');
        }
      } finally {
        setLoading(false);
      }
    }

    loadPreview();
    return () => controller.abort();
  }, [username, password]);

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');

    try {
      const response = await fetch('/api/v1/stream-ban/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not complete the ban.');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message || 'Could not complete the ban.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Confirm Stream Ban | NowTarot AI</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="ban-confirm-page">
        <div className="ban-confirm-card">
          <p className="ban-confirm-kicker">Moderation Action</p>
          <h1>Confirm stream ban</h1>

          {loading ? <p>Checking ban link…</p> : null}

          {!loading && error ? (
            <div className="ban-confirm-message ban-confirm-error">{error}</div>
          ) : null}

          {!loading && !error && !result && preview ? (
            <>
              <p className="ban-confirm-copy">
                Are you sure you want to ban <strong>@{preview.username}</strong> from the livestream?
              </p>
              <p className="ban-confirm-note">
                Once confirmed, their questions are rejected automatically. This does not block
                their IP on nowtarot.com.
              </p>
              {preview.already_banned ? (
                <div className="ban-confirm-message ban-confirm-warning">
                  This username is already banned. Confirming again keeps the ban in place.
                </div>
              ) : null}
              <button
                type="button"
                className="ban-confirm-button"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? 'Banning…' : 'Confirm stream ban'}
              </button>
            </>
          ) : null}

          {!loading && !error && result ? (
            <div className="ban-confirm-message ban-confirm-success">
              <strong>@{result.username}</strong>{' '}
              {result.already_banned
                ? 'was already banned from the livestream.'
                : 'is now banned from the livestream.'}
            </div>
          ) : null}
        </div>
      </main>
    </>
  );
};

export default StreamBanConfirm;
